// Last Minute V2 filter metadata — shared by the desktop filter popovers (9e)
// and the mobile bottom sheet (8b). Presentation-side only: everything here maps
// onto the EXISTING feed query params (position / age_min / age_max / mv_min /
// mv_max / salary_max) — no new BE behavior.

// Position groups (9e "group for speed, detail when it matters"). Detail codes
// are the artboard's chips; `be` is the exact snapshot string the BE `?position=`
// filter matches (case-insensitive exact). Codes without a platform position
// (LWB/RWB) are not listed — the BE has no snapshot value for them.
export const POSITION_GROUPS = [
  { key: "GK", label: "GK", details: [{ code: "GK", be: "Goalkeeper" }] },
  {
    key: "Defence",
    label: "Defence",
    details: [
      { code: "CB", be: "Centre-Back" },
      { code: "LB", be: "Left-Back" },
      { code: "RB", be: "Right-Back" },
    ],
  },
  {
    key: "Midfield",
    label: "Midfield",
    details: [
      { code: "DM", be: "Defensive Midfield" },
      { code: "CM", be: "Central Midfield" },
      { code: "AM", be: "Attacking Midfield" },
    ],
  },
  {
    key: "Attack",
    label: "Attack",
    details: [
      { code: "LW", be: "Left Winger" },
      { code: "RW", be: "Right Winger" },
      { code: "ST", be: "Striker" },
    ],
  },
];

// The group a BE position string belongs to (for re-opening the right detail row).
export const groupForPosition = (be) => {
  if (!be) return "";
  const g = POSITION_GROUPS.find((grp) => grp.details.some((d) => d.be.toLowerCase() === String(be).toLowerCase()));
  return g ? g.key : "";
};
// Short code for an active BE position ("Left-Back" → "LB") — filter tokens (1b).
export const codeForPosition = (be) => {
  for (const g of POSITION_GROUPS) {
    const d = g.details.find((x) => x.be.toLowerCase() === String(be || "").toLowerCase());
    if (d) return d.code;
  }
  return be || "";
};

// Market-value presets (9e — "replaces league levels, one comparable number").
// Values in absolute euros for the existing mv_min/mv_max params.
export const MV_PRESETS = [
  { id: "lt500k", label: "< €500k", min: null, max: 500000 },
  { id: "500k_2m", label: "€500k–2M", min: 500000, max: 2000000 },
  { id: "2_10m", label: "€2–10M", min: 2000000, max: 10000000 },
  { id: "10m_plus", label: "€10M+", min: 10000000, max: null },
];

// Salary-band presets (9e desktop / 8b mobile). The feed's only salary param is
// salary_max ("band lower bound within budget"), so each preset applies its UPPER
// bound; a lower bound needs a BE salary_min (see TODO.md).
export const SALARY_PRESETS = [
  { id: "10_20k", label: "€10–20k", max: 20000 },
  { id: "20_40k", label: "€20–40k", max: 40000 },
  { id: "40_60k", label: "€40–60k", max: 60000 },
  { id: "60_100k", label: "€60–100k", max: 100000 },
  { id: "100k_plus", label: "€100k+", max: null },
];

// Shorthand money parser (9e: "type 750k" / "1M" / "8M") → absolute euros, or
// null when empty/unparseable. A bare number ≤ 500 reads as millions (the old
// €M inputs' convention), anything larger as euros.
export const parseMoneyShorthand = (raw) => {
  const s = String(raw ?? "").trim().replace(/[€\s,]/g, "");
  if (!s) return null;
  const m = s.match(/^(\d+(?:\.\d+)?)([kKmM]?)$/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (Number.isNaN(n)) return null;
  if (m[2].toLowerCase() === "k") return Math.round(n * 1e3);
  if (m[2].toLowerCase() === "m") return Math.round(n * 1e6);
  return n <= 500 ? Math.round(n * 1e6) : Math.round(n);
};

// Compact band label for the live chip ("MV €1–8M", "€25–45k / mo").
const short = (v) => {
  if (v == null) return "";
  if (v >= 1e6) return `${(v / 1e6) % 1 === 0 ? v / 1e6 : (v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${Math.round(v / 1e3)}k`;
  return String(v);
};
export const bandChipLabel = (min, max, { prefix = "", suffix = "" } = {}) => {
  if (min == null && max == null) return "";
  if (min != null && max != null) return `${prefix}€${short(min)}–${short(max)}${suffix}`;
  if (min != null) return `${prefix}€${short(min)}+${suffix}`;
  return `${prefix}< €${short(max)}${suffix}`;
};

// Age slider bounds (8b) — the platform's sensible professional range.
export const AGE_BOUNDS = { min: 15, max: 40 };
